import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import Popup from 'semantic-ui-react/dist/commonjs/modules/Popup';
import Image from 'semantic-ui-react/dist/commonjs/elements/Image';
import { Field } from 'redux-form';
import { greaterThanZero, requiredWithoutZero } from '../../../utils/validation';
import renderTextField from '../../form-controls/RenderTextField';
import RenderCommonDropdownField from '../../../components/form-controls/RenderCommonDropdownField';
import { normalizeCurrency } from '../../../utils/validation/restrictions';
import {
  formatCurrencyAmount,
  parseCurrency,
  computeWithTxn,
  amountOrCurrencyChangeCT,
  getThresholdLimit,
  isNonHighValueOrDefaultTier
} from '../../../utils/library';
import { API_URL } from '../../../utils/config';
import './view-for-dashboard.css';
import {
  CORPORATE,
  FEE_BASED_MARKETS,
  FLAG_URL,
  HIGH_VALUE_TRANSACTION,
  INDIVIDUAL
} from '../../../utils/constants';
import { AppMigration } from '../../../containers/appMigration/AppMigration';
import { isAppMigrationEnabledContent } from '../../../redux/actions/user-status/user-status-action';
import { PARAMETER_CHANGE } from '../../../utils/constants/eventsConstants';
import {
  FeesIncludedText,
  StrikedText,
  FeesTitleText,
  FXRateContainer,
  DiscountedFeeText,
  FeeParentContainer,
  BoldCurrency
} from './Style';
import { BOLT_ICON } from '@Images';
import { CouponContainer } from '../coupon-container/CouponContainer';
import { useCouponData } from '../../../hooks/useCouponData';
import { PromotionMessage } from '../../offer-promotion-message';
import { getFlagValue } from '@Redux/actions/flagsmith';
import { getCountryCode } from '@Redux/selectors';

function ViewForDashboard(props) {
  let {
    DestinationErr,
    sourceErr,
    dispatch,
    showAppMigration,
    isAppMigrationEnabled,
    first_instarem_transaction,
    transaction_fee_amount,
    transactionDetails: { transaction_config, regular_transaction_fee_amount },
    source_currency,
    destination_currency,
    sourceCurrencyList,
    isNewCouponCodeApplied,
    net_source_amount,
    gross_source_amount,
    newCouponAmount,
    account_type,
    storedNonFormattedCouponData
  } = props;

  const { couponList, couponCMSData } = useCouponData({
    couponCMSData: props.couponCMSData,
    source_currency,
    source_amount: gross_source_amount,
    destination_currency,
    storedNonFormattedCouponData
  });

  const { total_fee_amount = 0, regular_total_fee_amount = 0 } = transaction_config || {};

  useEffect(() => {
    (async () => {
      await dispatch(isAppMigrationEnabledContent());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAppMigrationEnabled, transaction_config?.app_fee_difference]);

  const trackAndCompute = (event_name, amount, isSourceAmountChanged) => {
    amountOrCurrencyChangeCT(amount, event_name);
    //Compute new values
    computeWithTxn(props.mainConverter, isSourceAmountChanged);
  };

  const renderFxRateDetails = () => {
    const {
      transactionDetails: { instarem_fx_rate, regular_instarem_fx_rate },
      fx_rate_last_updated
    } = props;
    const FxRatePopupContent = () => {
      return <span>{fx_rate_last_updated}</span>;
    };

    const isDisountApplicable =
      regular_instarem_fx_rate > 0 &&
      instarem_fx_rate > 0 &&
      regular_instarem_fx_rate !== instarem_fx_rate;

    return (
      <FXRateContainer id="rate-conversion">
        <BoldCurrency $isBold={isDisountApplicable}>{`1 ${props.source_currency}`}</BoldCurrency>
        <span>&nbsp;=&nbsp;</span>
        {isDisountApplicable ? (
          <StrikedText id="dashboard-regular-instarem-fx-rate">
            {`${props.regular_instarem_fx_rate}`}
          </StrikedText>
        ) : null}
        <BoldCurrency
          id="dashboard-instarem-fx-rate"
          className="bottomvarbar-line-currency"
          $isBold={isDisountApplicable}
        >{`${props.instarem_fx_rate} ${props.destination_currency}`}</BoldCurrency>
        <Popup
          id="last-update"
          hideOnScroll={true}
          trigger={<Image src={BOLT_ICON} className="bolt" />}
          content={<FxRatePopupContent />}
        />
      </FXRateContainer>
    );
  };

  const { tier } = props?.transaction_config || {};
  const isFeeDiscountApplicable =
    Number(regular_total_fee_amount) > Number(total_fee_amount) &&
    Number(regular_transaction_fee_amount) >= Number(transaction_fee_amount);
  const { instarem_fx_rate, regular_instarem_fx_rate } = props;
  const isFxDiscountApplicable =
    instarem_fx_rate && regular_instarem_fx_rate && instarem_fx_rate !== regular_instarem_fx_rate;

  const isNthTransactionApplicable =
    (tier?.includes('NTH_TRANSACTION') || (tier && isNonHighValueOrDefaultTier(tier))) &&
    (isFxDiscountApplicable ||
      (total_fee_amount &&
        regular_total_fee_amount &&
        total_fee_amount !== regular_total_fee_amount));
  const isFeeBasedMarket = FEE_BASED_MARKETS.includes(props.user_session?.country_code || '');
  const thresholdValues = getFlagValue(HIGH_VALUE_TRANSACTION);
  const countryCode = useSelector(getCountryCode);
  const hvtThresholdLimit = getThresholdLimit(thresholdValues, countryCode, props?.source_currency);
  const isDiscountApplied = isFeeDiscountApplicable || isFxDiscountApplicable;

  return (
    <div className="container vertical horizontal-convertor">
      <h2 className="weight-1 center-align-text">Transfer</h2>
      {showAppMigration && <AppMigration className="appMigration-margin" />}
      <div className="container horizontal no-padding1 uppervarbar">
        <div className="weight-1 container vertical downvarbar leftbar">
          <div className={'downvarbar-txt'}>You send</div>
          <div className="container horizontal dropdownvarbar-dropdown">
            {props.source_currency && (
              <Image
                src={`${FLAG_URL}${props.source_currency}.png`.toLocaleLowerCase()}
                className="label-img center-self margin-16"
                width="32"
                height="32"
              />
            )}
            <Field
              bindToThisObject={props.source_currency}
              name="source_currency"
              component={RenderCommonDropdownField}
              onDropdownChange={(obj) =>
                amountOrCurrencyChangeCT(obj.value, PARAMETER_CHANGE.SOURCE_CURRENCY_CHANGE)
              }
              fluid={true}
              isSearchable={false}
              className="dropdown-component borderafter"
              staticValues={sourceCurrencyList}
              showImage={true}
            />
          </div>
          <Field
            showLoader={props.showLoader}
            name="gross_source_amount"
            type="text"
            errFromParent={sourceErr}
            className="converter-input borderafter1"
            component={renderTextField}
            validate={[requiredWithoutZero, greaterThanZero]}
            normalize={normalizeCurrency}
            disableUnderlineStyling={true}
            format={formatCurrencyAmount}
            parse={parseCurrency}
            onChange={(e) =>
              trackAndCompute(PARAMETER_CHANGE.SOURCE_AMOUNT_CHANGE, e.target.value, true)
            }
          />
        </div>
        <div className="weight-1 container vertical  downvarbar rightbar">
          <div className={'downvarbar-txt'}>They receive</div>
          <div className="container horizontal dropdownvarbar-dropdown">
            {props.destination_currency && (
              <Image
                src={`${FLAG_URL}${props.destination_currency}.png`.toLocaleLowerCase()}
                className="label-img center-self margin-16"
                width="32"
                height="32"
              />
            )}
            <Field
              bindToThisObject={props.destination_currency}
              name="destination_currency"
              component={RenderCommonDropdownField}
              onDropdownChange={(obj) =>
                amountOrCurrencyChangeCT(obj.value, PARAMETER_CHANGE.DESTINATION_CURRENCY_CHANGE)
              }
              className="dropdown-component "
              fluid={true}
              dependedOnObject={props.source_currency || false}
              // eslint-disable-next-line max-len
              url={`${API_URL}/public/remitter/currencies/dropdown?country_code=${props.user_session.country_code}&source_currency=${props.source_currency}`}
              isSearchable={true}
              showImage={true}
            />
          </div>
          <Field
            showLoader={props.showLoader}
            name="destination_amount"
            type="text"
            errFromParent={DestinationErr}
            className="converter-input"
            component={renderTextField}
            disableUnderlineStyling={true}
            validate={[requiredWithoutZero, greaterThanZero]}
            normalize={normalizeCurrency}
            format={formatCurrencyAmount}
            parse={parseCurrency}
            onChange={(e) =>
              trackAndCompute(PARAMETER_CHANGE.DESTINATION_AMOUNT_CHANGE, e.target.value, false)
            }
          />
        </div>
      </div>
      {account_type === INDIVIDUAL && (
        <CouponContainer
          isDashboardScreen={true}
          couponList={couponList}
          sourceErr={sourceErr}
          destErr={DestinationErr}
          netSourceAmount={net_source_amount}
          sourceCurrency={source_currency}
          grossSourceAmount={gross_source_amount}
          isNewCouponCodeApplied={isNewCouponCodeApplied}
          newCouponAmount={newCouponAmount}
          couponCMSData={couponCMSData}
          dispatch={dispatch}
        />
      )}
      <div className="container horizontal space-between bottomvarbar">
        {props.source_currency && props.destination_currency && props.instarem_fx_rate ? (
          <div className="container-start horizontal">
            <div className="container vertical">{renderFxRateDetails()}</div>
          </div>
        ) : null}
        <FeeParentContainer>
          <FeesIncludedText>
            <FeesTitleText $isBold={isFeeDiscountApplicable}>Fees Included = </FeesTitleText>
            {isFeeDiscountApplicable ? (
              <StrikedText id="dashboard-regular-total-fee">{regular_total_fee_amount}</StrikedText>
            ) : null}

            <DiscountedFeeText id="dashboard-total-fee" $isBold={isFeeDiscountApplicable}>
              {`${`${total_fee_amount}`.toLocaleString('en')} ${props.source_currency || ''}`}
            </DiscountedFeeText>
          </FeesIncludedText>
        </FeeParentContainer>
      </div>
      {!sourceErr && !DestinationErr && account_type !== CORPORATE ? (
        <PromotionMessage
          id="dashboard-promotion-message"
          isDiscountApplied={isDiscountApplied}
          discountTagTopMargin={-28}
          discountTagFlexStartAlignment={!isFeeBasedMarket}
          firstTimeTransfer={first_instarem_transaction}
          isNthTransactionApplicable={isNthTransactionApplicable}
          marginBottom={32}
          hvtThresholdLimit={hvtThresholdLimit}
          grossSourceAmount={gross_source_amount}
          sourceCurrency={source_currency}
          tier={tier}
        />
      ) : null}
    </div>
  );
}

ViewForDashboard.propTypes = {
  showLoader: PropTypes.bool,
  user_session: PropTypes.object,
  source_currency: PropTypes.string,
  onSourceAmountChange: PropTypes.func,
  destination_currency: PropTypes.string,
  onSourceCurrencyChange: PropTypes.func,
  onDestinationAmountChange: PropTypes.func,
  onDestinationCurrencyChange: PropTypes.func,
  instarem_fx_rate: PropTypes.number,
  regular_instarem_fx_rate: PropTypes.number,
  first_instarem_transaction: PropTypes.bool,
  transaction_fee_amount: PropTypes.number,
  fx_rate_last_updated: PropTypes.string,
  DestinationErr: PropTypes.string,
  sourceErr: PropTypes.string,
  mainConverter: PropTypes.any,
  showAppMigration: PropTypes.bool,
  dispatch: PropTypes.func,
  isAppMigrationEnabled: PropTypes.bool,
  transaction_config: PropTypes.object,
  first_transaction_fee_config: PropTypes.object,
  gross_source_amount: PropTypes.number,
  transactionDetails: PropTypes.object,
  sourceCurrencyList: PropTypes.array,
  couponCMSData: PropTypes.object,
  isNewCouponCodeApplied: PropTypes.bool,
  net_source_amount: PropTypes.number,
  newCouponAmount: PropTypes.number,
  storedNonFormattedCouponData: PropTypes.object,
  account_type: PropTypes.string
};

function mapStateToProps(reduxState) {
  const {
    rootReducer: {
      transactionReducer: {
        currencyCovertor: { is_destination_amount_last_changed, sourceErr, DestinationErr },
        transactionDetails: {
          transaction_config,
          first_transaction_fee_config,
          source_currency,
          gross_source_amount,
          first_instarem_transaction,
          net_source_amount
        },
        couponCMSData,
        storedNonFormattedCouponData,
        isNewCouponCodeApplied,
        newCouponAmount
      },
      userStatusReducer: {
        showAppMigration,
        isAppMigrationEnabled,
        sourceCurrencyList,
        account_type
      }
    }
  } = reduxState;
  return {
    is_destination_amount_last_changed,
    sourceErr,
    DestinationErr,
    showAppMigration,
    isAppMigrationEnabled,
    transaction_config,
    first_transaction_fee_config,
    source_currency,
    gross_source_amount,
    first_instarem_transaction,
    sourceCurrencyList,
    couponCMSData,
    isNewCouponCodeApplied,
    net_source_amount,
    newCouponAmount,
    storedNonFormattedCouponData,
    account_type
  };
}

export default connect(mapStateToProps)(ViewForDashboard);
